import { SUPPORTED_LANGUAGES, isRightToLeft, type SupportedLanguage } from '@foyer/shared';
import { ar } from './ar.js';
import { de } from './de.js';
import { en } from './en.js';
import { fr } from './fr.js';
import { it } from './it.js';
import { nl } from './nl.js';
import { ro } from './ro.js';
import { ru } from './ru.js';
import { tr } from './tr.js';
import type { Strings } from './strings.js';

/**
 * Kiosk translations.
 *
 * No i18n framework: the kiosk has a fixed, small set of strings and must boot
 * offline in under a second. Everything the *tenant* configures (visitor types,
 * reasons, field labels, the greeting) carries its own `*I18n` map from the
 * server, so adapting the wording is a configuration change, not a release.
 *
 * One file per language, all of them typed against `Strings`: a missing key
 * fails the build rather than showing a blank line on a tablet in a hallway.
 */

export type { Strings } from './strings.js';

export const LANGUAGES = SUPPORTED_LANGUAGES.map((entry) => entry.code);
export type Language = SupportedLanguage;

/**
 * Keyed by the shared catalogue rather than by `string`: adding a language
 * there without shipping its strings then fails the build instead of silently
 * offering a switch that falls back to German on the tablet.
 */
const TRANSLATIONS: Record<SupportedLanguage, Strings> = { de, en, tr, fr, nl, ar, ro, it, ru };

export function strings(language: string): Strings {
  return TRANSLATIONS[language as SupportedLanguage] ?? de;
}

export function hasTranslation(language: string): boolean {
  return TRANSLATIONS[language as SupportedLanguage] !== undefined;
}

/**
 * Applies the text direction of the chosen language to the document. Arabic is
 * right to left; the layout itself is direction agnostic (logical properties),
 * so this attribute is all it takes to mirror the kiosk correctly.
 */
export function applyDirection(language: string): void {
  const root = document.documentElement;
  root.lang = language;
  root.dir = isRightToLeft(language) ? 'rtl' : 'ltr';
}

/** Picks the localised variant of a tenant-configured label. */
export function localized(value: string, i18n: Record<string, string> | undefined, language: string): string {
  const translated = i18n?.[language];
  // An empty string in the map means "not translated yet", not "show nothing".
  return translated && translated.trim() ? translated : value;
}
